import type {ComponentStageData, ComponentStageHeuristicDefinition, ConversionDal} from '../../types'
import {START, END, CENTER} from './constants'
import type {Pointer} from '@wix/document-services-types'
import {
    isHeaderFirstRowChildComponent,
    isInternalBox,
    isSuperGridCell,
    isPlugin,
    updateTargetLayout,
    isMenuContainerChild,
    isChildOfHeader
} from '../common'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {getStructurePointer, getTargetLayoutPointer, getStylePointer} from '../../conversionDal/utils/pointer'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {ComponentAlignment} from './types'
import _ from 'lodash'
import type {StyleEntryItem} from '../../conversionDal/types'
// eslint-disable-next-line wix-editor/no-internal-import
import {CSS_PROPERTIES} from '@wix/component-protocol/schema'

const updateTextAlignProperties = (
    conversionDal: ConversionDal,
    compStylePointer: Pointer,
    baseStyleSettings: StyleEntryItem,
    alignment: ComponentAlignment
) => {
    if (
        baseStyleSettings?.cssProperties?.textAlign ||
        baseStyleSettings?.metaData?.default?.cssProperties?.hasOwnProperty?.('textAlign')
    ) {
        conversionDal.modify(compStylePointer, (value: any) =>
            _.merge({}, value, {
                cssProperties: {
                    textAlign: alignment
                }
            })
        )
    }

    const customPropertiesInfo = baseStyleSettings?.metaData?.cssCustomPropertiesInfo || {}

    Object.entries(customPropertiesInfo).forEach(([customPropKey, propertyType]) => {
        if (propertyType === CSS_PROPERTIES.CSS_PROPERTY_TYPE.textAlign) {
            conversionDal.modify(compStylePointer, (value: any) =>
                _.merge({}, value, {
                    cssCustomProperties: {
                        [customPropKey]: alignment
                    }
                })
            )
        }
    })
}

const updateComponentAlignment = (componentStageData: ComponentStageData, alignment: ComponentAlignment): void => {
    const {componentPointer, conversionDal, initialSnapshot} = componentStageData
    const {semanticType} = conversionDal.get(componentPointer)
    const {itemLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer)))
    itemLayout.justifySelf = alignment
    updateTargetLayout(componentStageData, {itemLayout})

    if (semanticType === SemanticTypes.TEXT) {
        const compStylePointer = getStylePointer(componentPointer)
        const baseStyleSettings = initialSnapshot.getValue(compStylePointer) as StyleEntryItem

        if (baseStyleSettings) {
            updateTextAlignProperties(conversionDal, compStylePointer, baseStyleSettings, alignment)
        }
    }
}

const checkParentAlignment = (
    componentPointer: Pointer,
    conversionDal: ConversionDal,
    alignmentDirection: ComponentAlignment
): boolean => {
    const {parent} = conversionDal.get(componentPointer)
    const {alignment} = conversionDal.get(getStructurePointer({id: parent})) ?? {alignment: CENTER}
    return alignment === alignmentDirection
}

export const alignmentHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData) => {
            updateComponentAlignment(componentStageData, CENTER)
        },
        heuristicCode: 'MENU CONTAINER CHILD ALIGNMENT'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, config} = componentStageData
            return (
                componentPointer.id === config.rootPointer.id ||
                isInternalBox(componentStageData) ||
                isSuperGridCell(componentStageData) ||
                isPlugin(componentStageData)
            )
        },
        apply: () => {}
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            return isHeaderFirstRowChildComponent(componentPointer, conversionDal)
        },
        apply: () => {}
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            return isChildOfHeader(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData) => {
            updateComponentAlignment(componentStageData, CENTER)
        },
        heuristicCode: 'COMPONENT IN HEADER BUT NOT IN FIRST ROW'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, START)
        },
        apply: (componentStageData: ComponentStageData) => updateComponentAlignment(componentStageData, START),
        heuristicCode: 'COMPONENTS ALIGNED START'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, END)
        },
        apply: (componentStageData: ComponentStageData) => updateComponentAlignment(componentStageData, END),
        heuristicCode: 'COMPONENTS ALIGNED END'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, CENTER)
        },
        apply: (componentStageData: ComponentStageData) => updateComponentAlignment(componentStageData, CENTER),
        heuristicCode: 'COMPONENTS MIXED ALIGNMENT'
    }
]
